/**
 * Bounded audio preloader for flashcard audio.
 *
 * Audio is fetched once, kept as an in-memory object URL while it is hot, and
 * also allowed to populate the browser's HTTP cache. Requests for the same URL
 * share one promise so reactive watchers cannot accidentally download it more
 * than once.
 */

/* eslint-disable no-console */

export function useAudioPreloader(options = {}) {
  const {
    maxEntries = 40,
    concurrency = 3,
    useLinkHints = false,
    onDebug = null,
  } = options;

  const cache = new Map();
  const queue = [];
  const preloadLinks = new Map();
  let active = 0;
  let disposed = false;

  function debug(...args) {
    try {
      if (typeof onDebug === "function") onDebug(...args);
    } catch {
      // Debug hooks must never affect loading.
    }
  }

  function hasDOM() {
    return typeof window !== "undefined" && typeof document !== "undefined";
  }

  function ensureLinkPreload(url) {
    if (!useLinkHints || !hasDOM() || preloadLinks.has(url)) return;
    try {
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "audio";
      link.href = url;
      document.head.appendChild(link);
      preloadLinks.set(url, link);
    } catch {
      // The fetch/audio-element paths below remain available.
    }
  }

  function releaseEntry(entry) {
    try {
      entry?.controller?.abort();
    } catch {
      // Ignore already-finished requests.
    }
    try {
      if (entry?.objectUrl && typeof URL !== "undefined") {
        URL.revokeObjectURL(entry.objectUrl);
      }
    } catch {
      // Ignore object URL cleanup failures.
    }
    try {
      if (entry?.audio) {
        entry.audio.removeAttribute("src");
        entry.audio.load?.();
      }
    } catch {
      // Ignore media cleanup failures.
    }
  }

  function enforceLimit() {
    while (cache.size > maxEntries) {
      const firstReady = Array.from(cache.entries()).find(
        ([, entry]) => entry.status !== "loading"
      );
      if (!firstReady) return;
      const [url, entry] = firstReady;
      cache.delete(url);
      releaseEntry(entry);
      debug("[AudioPreloader] evicted", url);
    }
  }

  function lruTouch(url) {
    const entry = cache.get(url);
    if (!entry) return;
    cache.delete(url);
    cache.set(url, entry);
    enforceLimit();
  }

  async function fetchIntoMemory(url, entry) {
    if (typeof fetch !== "function") return null;

    const controller =
      typeof AbortController !== "undefined" ? new AbortController() : null;
    entry.controller = controller;

    const response = await fetch(url, {
      mode: "cors",
      credentials: "omit",
      cache: "force-cache",
      ...(controller ? { signal: controller.signal } : {}),
    });
    if (!response.ok) {
      throw new Error(`Audio preload failed with HTTP ${response.status}`);
    }

    const blob = await response.blob();
    if (
      !blob.size ||
      typeof URL === "undefined" ||
      typeof URL.createObjectURL !== "function"
    ) {
      return null;
    }

    return URL.createObjectURL(blob);
  }

  function preloadWithAudioElement(url) {
    if (!hasDOM() || typeof Audio === "undefined") {
      return Promise.resolve(null);
    }

    return new Promise((resolve, reject) => {
      let audio;
      let settled = false;
      let timeoutId = null;

      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
        try {
          audio?.removeEventListener("canplay", onReady);
          audio?.removeEventListener("canplaythrough", onReady);
          audio?.removeEventListener("error", onError);
        } catch {
          // Ignore listener cleanup failures.
        }
      };
      const finish = (callback, value) => {
        if (settled) return;
        settled = true;
        cleanup();
        callback(value);
      };
      const onReady = () => finish(resolve, audio);
      const onError = (event) =>
        finish(reject, event?.error || new Error("Audio preload failed"));

      try {
        audio = new Audio();
        audio.preload = "auto";
        audio.crossOrigin = "anonymous";
        audio.addEventListener("canplay", onReady, { once: true });
        audio.addEventListener("canplaythrough", onReady, { once: true });
        audio.addEventListener("error", onError, { once: true });
        audio.src = url;
        audio.load();
        timeoutId = setTimeout(
          () => finish(reject, new Error("Audio preload timed out")),
          15000
        );
      } catch (error) {
        finish(reject, error);
      }
    });
  }

  async function load(url, entry) {
    ensureLinkPreload(url);

    try {
      const objectUrl = await fetchIntoMemory(url, entry);
      if (objectUrl) {
        entry.objectUrl = objectUrl;
        entry.playbackUrl = objectUrl;
        return;
      }
    } catch (error) {
      if (disposed || error?.name === "AbortError") throw error;
      debug("[AudioPreloader] fetch fallback", url, error);
    } finally {
      entry.controller = null;
    }

    const audio = await preloadWithAudioElement(url);
    entry.audio = audio;
    entry.playbackUrl = url;
  }

  function startNext() {
    if (disposed || active >= concurrency) return;
    const url = queue.shift();
    if (!url) return;

    const entry = cache.get(url);
    if (!entry || entry.status !== "loading") {
      startNext();
      return;
    }

    active += 1;
    debug("[AudioPreloader] loading", url);
    load(url, entry)
      .then(() => {
        if (disposed || cache.get(url) !== entry) return;
        entry.status = "ready";
        entry.error = null;
        lruTouch(url);
        debug("[AudioPreloader] ready", url);
      })
      .catch((error) => {
        if (cache.get(url) !== entry) return;
        entry.status = "error";
        entry.error = error;
        debug("[AudioPreloader] error", url, error);
      })
      .finally(() => {
        entry.resolve?.();
        entry.resolve = null;
        active -= 1;
        enforceLimit();
        startWorkers();
      });
  }

  function startWorkers() {
    while (!disposed && active < concurrency && queue.length > 0) {
      startNext();
    }
  }

  function preload(urls) {
    if (disposed) return Promise.resolve([]);

    const list = [
      ...new Set((Array.isArray(urls) ? urls : [urls]).filter(Boolean)),
    ];
    const promises = list.map((url) => {
      const existing = cache.get(url);
      if (existing?.status === "ready") {
        lruTouch(url);
        return Promise.resolve();
      }
      if (existing?.status === "loading") return existing.promise;

      let resolve;
      const promise = new Promise((done) => {
        resolve = done;
      });
      cache.set(url, {
        status: "loading",
        audio: null,
        objectUrl: null,
        playbackUrl: url,
        controller: null,
        error: null,
        promise,
        resolve,
      });
      queue.push(url);
      return promise;
    });

    startWorkers();
    return Promise.allSettled(promises);
  }

  function get(url) {
    if (!url) return null;
    const entry = cache.get(url);
    if (!entry || entry.status !== "ready") return null;
    lruTouch(url);
    return {
      warmed: true,
      element: entry.audio || null,
      url: entry.playbackUrl || url,
    };
  }

  function queueForCards(cards = [], opts = {}) {
    const { index = 0, ahead = 6, behind = 1 } = opts;
    const current = Math.max(0, Math.floor(index));
    const lo = Math.max(0, current - Math.max(0, behind));
    const hi = Math.min(cards.length - 1, current + Math.max(0, ahead));
    const indices = [];

    // Current and upcoming audio matter most; previously viewed cards are last.
    for (let i = current; i <= hi; i++) indices.push(i);
    for (let i = lo; i < current; i++) indices.push(i);

    const urls = [];
    for (const i of indices) {
      const card = cards[i];
      if (!card) continue;
      if (card.language_sentence_audio) urls.push(card.language_sentence_audio);
      if (card.language_word_audio) urls.push(card.language_word_audio);
    }

    const prioritizedUrls = [...new Set(urls)];
    // An index change may make an already-queued URL urgent. Move those jobs
    // ahead of stale look-ahead work while leaving active requests alone.
    for (let i = prioritizedUrls.length - 1; i >= 0; i--) {
      const queuedIndex = queue.indexOf(prioritizedUrls[i]);
      if (queuedIndex === -1) continue;
      queue.splice(queuedIndex, 1);
      queue.unshift(prioritizedUrls[i]);
    }

    return preload(prioritizedUrls);
  }

  function dispose() {
    if (disposed) return;
    disposed = true;
    queue.length = 0;

    for (const entry of cache.values()) {
      releaseEntry(entry);
      entry.resolve?.();
    }
    cache.clear();

    for (const link of preloadLinks.values()) {
      try {
        link.remove();
      } catch {
        // Ignore detached links.
      }
    }
    preloadLinks.clear();
  }

  return { preload, get, queueForCards, dispose };
}
